import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';
import PasswordInput from '../components/PasswordInput';
import { getPasswordPolicyError, PASSWORD_POLICY_MESSAGE } from '../utils/passwordPolicy';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const tokenFromFragment = new URLSearchParams(location.hash.replace(/^#/, '')).get('token') || '';
  const resetTokenRef = useRef('');
  const handledTokenRef = useRef('');
  if (tokenFromFragment) resetTokenRef.current = tokenFromFragment;
  const [tokenState, setTokenState] = useState('checking');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordPolicyError, setPasswordPolicyError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    // Read the fragment on every location change. The component can remain mounted
    // when a user opens a newer reset link in the same tab.
    if (!tokenFromFragment || handledTokenRef.current === tokenFromFragment) {
      if (!tokenFromFragment && !handledTokenRef.current) setTokenState('invalid');
      return undefined;
    }
    const resetToken = tokenFromFragment;
    handledTokenRef.current = resetToken;
    resetTokenRef.current = resetToken;
    setTokenState('checking');
    setError('');
    setComplete(false);
    let cancelled = false;
    const validateToken = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/auth/password-reset/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetToken }),
        });
        const data = await response.json();
        if (!cancelled) {
          setTokenState(response.ok && data.valid ? 'valid' : 'invalid');
        }
      } catch (_requestError) {
        if (!cancelled) setTokenState('invalid');
      } finally {
        if (!cancelled) navigate('/reset-password', { replace: true });
      }
    };
    validateToken();
    return () => { cancelled = true; };
  }, [navigate, tokenFromFragment]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    const policyError = getPasswordPolicyError(newPassword);
    if (policyError) {
      setPasswordPolicyError(policyError);
      setError(policyError);
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        token: resetTokenRef.current,
        new_password: newPassword,
        confirm_new_password: confirmNewPassword,
      };
      const response = await apiFetch(`${API_BASE_URL}/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to reset your password');
      setNewPassword('');
      setConfirmNewPassword('');
      setComplete(true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="public-page">
      <div className="login-section">
        <div className="login-container password-reset-container">
          <div className="login-card">
            <h1 className="login-title">Choose a New Password</h1>
            {tokenState === 'checking' && <p className="login-subtitle">Checking your secure reset link…</p>}
            {tokenState === 'invalid' && (
              <>
                <div className="login-error" role="alert">This password reset link is invalid or expired.</div>
                <p className="login-footer-text"><Link className="login-signup-link" to="/forgot-password">Request a new reset link</Link></p>
              </>
            )}
            {tokenState === 'valid' && complete && (
              <>
                <div className="admin-quality-success" role="status">Your password was changed. Sign in again with your new password.</div>
                <p className="login-footer-text"><Link className="login-button login-button-link" to="/login">Return to Sign In</Link></p>
              </>
            )}
            {tokenState === 'valid' && !complete && (
              <>
                {error && <div className="login-error" role="alert">{error}</div>}
                <form className="login-form" onSubmit={handleSubmit}>
                  <div className="login-form-group">
                    <label className="login-label" htmlFor="password-reset-new">New password</label>
                    <PasswordInput
                      id="password-reset-new"
                      label="new password"
                      autoComplete="new-password"
                      minLength={8}
                      required
                      value={newPassword}
                      onChange={(event) => {
                        const nextPassword = event.target.value;
                        setNewPassword(nextPassword);
                        const nextPolicyError = getPasswordPolicyError(nextPassword);
                        setPasswordPolicyError(nextPolicyError);
                        if (!nextPolicyError) setError('');
                      }}
                    />
                    <p className="password-policy-hint">{PASSWORD_POLICY_MESSAGE}</p>
                    {passwordPolicyError && <p className="password-policy-error" role="alert">{passwordPolicyError}</p>}
                  </div>
                  <div className="login-form-group">
                    <label className="login-label" htmlFor="password-reset-confirm">Confirm new password</label>
                    <PasswordInput id="password-reset-confirm" label="confirm new password" autoComplete="new-password" minLength={8} required value={confirmNewPassword} onChange={(event) => setConfirmNewPassword(event.target.value)} />
                  </div>
                  <button className="login-button" type="submit" disabled={loading}>{loading ? 'Changing Password…' : 'Change Password'}</button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
